import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PCAP, SERVER_NAME } from './config';
import { useAuth } from './auth/AuthContext';
import { GraphComponent } from './GraphComponent';
import { NodeList } from './NodeLists';
import { getField, toNumber, toPortString } from './pcap/pcapCsvFields';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 1480,
    margin: '0 auto',
    minHeight: 0,
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'hidden',
    textAlign: 'left',

    [theme.breakpoints.down('sm')]: {
      padding: '4px 6px 0px',
    },
  },

  // HERO (match /stories)
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,
    textAlign: 'left',

    [theme.breakpoints.down('sm')]: {
      padding: '6px 8px 8px',
      marginBottom: 8,
    },
  },
  heroTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  heroTitle: {
    fontSize: '2.15rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  heroSubtitle: {
    marginTop: 6,
    color: '#cfd8ff',
    maxWidth: 900,
    lineHeight: 1.55,
    fontSize: 13,
  },
  heroMetaRow: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    color: '#9ea4d8',
    fontSize: 12,
  },
  heroMetaChip: {
    height: 22,
    fontSize: 11,
    backgroundColor: 'rgba(77, 121, 255, 0.12)',
    border: '1px solid rgba(77, 121, 255, 0.35)',
    color: '#e5e7ff',
  },

  ghostButton: {
    textTransform: 'none',
    fontWeight: 700,
    color: '#cfd3ff',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: 10,
    padding: '10px 14px',
  },

  // MAIN GRID (match /stories)
  mainGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gridTemplateRows: 'minmax(0, 1fr)',
    gap: 18,
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    alignItems: 'stretch',

    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: 12,
    },
  },

  // FEED PANELS (match /stories list)
  feedPanel: {
    marginTop: 6,
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
  },
  feedHeader: {
    padding: '10px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#e5e7ff',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  sectionHint: {
    fontSize: 12,
    color: '#9ea4d8',
  },
  feedBody: {
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flex: 1,
    minHeight: 0,
    touchAction: 'pan-y',
    overscrollBehaviorX: 'none',
  },
  feedRow: {
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(5, 8, 20, 0.62)',
    padding: 10,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    cursor: 'default',
    '&:hover': { borderColor: 'rgba(77,121,255,0.4)' },
    textAlign: 'left',
  },
  feedRowTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
    color: '#e5e7ff',
    textAlign: 'left',
  },
  feedRowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    fontSize: 11,
    color: '#9ea4d8',
    textAlign: 'left',
  },
  chipType: {
    height: 20,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    backgroundColor: 'rgba(77, 121, 255, 0.15)',
    border: '1px solid rgba(77, 121, 255, 0.45)',
    color: '#ffe55c',
    fontWeight: 800,
  },
  chipChain: {
    height: 20,
    fontSize: 10,
    backgroundColor: 'rgba(15, 205, 255, 0.10)',
    border: '1px solid rgba(15, 205, 255, 0.35)',
    color: '#cfd3ff',
    fontWeight: 700,
  },
  subtle: {
    opacity: 0.92,
    color: '#e8f5ff',
  },

  kv: {
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    opacity: 0.92,
  },
  error: {
    color: '#ff7777',
    whiteSpace: 'pre-wrap',
    marginTop: 10,
  },

  // FORENSICS PANEL (match /stories evidence summary panel)
  forensicsPanel: {
    minHeight: 0,
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.45)',
    background: 'radial-gradient(circle at top left, rgba(77,121,255,0.22), rgba(5,8,20,0.98))',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    overflow: 'hidden',
    textAlign: 'left',
  },
  forensicsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  forensicsTitle: {
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    color: '#e5e7ff',
  },
  forensicsTokenLabel: {
    fontSize: 11,
    color: '#9ea4d8',
  },
  forensicsTokenValue: {
    fontSize: 12,
    fontFamily: 'monospace',
    backgroundColor: 'rgba(5,8,20,0.9)',
    borderRadius: 8,
    padding: '6px 8px',
    wordBreak: 'break-all',
    border: '1px solid rgba(158, 196, 255, 0.20)',
  },

  // Evidence split (match thesis/stories split feel)
  evidenceSplitRoot: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  evidenceGraphWrap: {
    minHeight: 0,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: 'rgba(5,8,20,0.95)',
    border: '1px solid rgba(255,255,255,0.10)',
    position: 'relative',
  },
  evidenceSplitter: {
    height: 10,
    flex: '0 0 auto',
    cursor: 'row-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    touchAction: 'none',
  },
  evidenceSplitterBar: {
    height: 4,
    width: '60%',
    borderRadius: 999,
    background: 'rgba(158, 196, 255, 0.35)',
  },
  evidenceListWrap: {
    minHeight: 0,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: 'black',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(255,255,255,0.10)',
  },
  evidenceListBody: {
    overflow: 'hidden',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    flex: 1,
    minHeight: 0,
  },

  th: {
    color: '#e5e7ff',
    fontWeight: 900,
    borderBottom: '1px solid rgba(255,255,255,0.10)',
    background: '#000',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    fontSize: 11,
    paddingTop: 6,
    paddingBottom: 6,
  },
  td: {
    color: '#ffffff',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    fontSize: 11,
    paddingTop: 6,
    paddingBottom: 6,
  },
}));

function safeJsonParse(s) {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function buildPcapGraphFromRows(rows) {
  const csvRows = Array.isArray(rows) ? rows : [];

  const nodesSet = new Set();
  const nodes = [];
  const links = [];
  const linkMap = new Map();
  const neighborMap = new Map();

  const macRegex = /^([0-9A-Fa-f]{2}([:-])){5}[0-9A-Fa-f]{2}$/;
  const addNeighbor = (a, b) => {
    if (!neighborMap.has(a)) neighborMap.set(a, new Set());
    if (!neighborMap.has(b)) neighborMap.set(b, new Set());
    neighborMap.get(a).add(b);
    neighborMap.get(b).add(a);
  };

  const ensureNode = (id) => {
    const key = String(id || '').trim();
    if (!key) return;
    if (nodesSet.has(key)) return;
    nodes.push({
      id: key,
      label: key,
      symbol: key,
      sentBytes: 0,
      receivedBytes: 0,
      totalBytes: 0,
      linkCount: 0,
      metaType: 'pcap',
    });
    nodesSet.add(key);
  };

  const addMacNode = (mac) => {
    const m = String(mac || '').trim();
    if (!m || !macRegex.test(m)) return;
    if (nodesSet.has(m)) return;
    nodes.push({
      id: m,
      label: m,
      symbol: m,
      sentBytes: 0,
      receivedBytes: 0,
      totalBytes: 0,
      linkCount: 0,
      metaType: 'pcap_mac',
    });
    nodesSet.add(m);
  };

  const linkMacToIp = (mac, ip, packets) => {
    const m = String(mac || '').trim();
    const i = String(ip || '').trim();
    if (!m || !i) return;
    if (!macRegex.test(m)) return;
    if (macRegex.test(i)) return;
    const key = `${m}↔${i}`;
    const existing = linkMap.get(key);
    if (existing) {
      existing.packets = (existing.packets || 0) + (Number(packets) || 0);
    } else {
      const linkObj = {
        id: `mac:${m}->ip:${i}`,
        source: m,
        target: i,
        value: 0,
        gas: 0,
        metaType: 'pcap_mac',
        pcapDir: 'mac',
        packets: Number(packets) || 0,
        curvature: 0,
      };
      linkMap.set(key, linkObj);
      links.push(linkObj);
    }
    addNeighbor(m, i);
  };

  csvRows.forEach((row) => {
    const clientAddr = getField(row, ['Client Addr', 'src', 'Src', 'source', 'Source']);
    const serverAddr = getField(row, ['Server Addr', 'dst', 'Dst', 'target', 'Target']);
    if (!clientAddr || !serverAddr) return;

    const a = String(clientAddr).trim();
    const b = String(serverAddr).trim();
    if (!a || !b) return;

    const clientMacRaw = getField(row, ['Client MAC', 'client_mac', 'ClientMac', 'src mac', 'Src MAC', 'src_mac']);
    const serverMacRaw = getField(row, ['Server MAC', 'server_mac', 'ServerMac', 'dst mac', 'Dst MAC', 'dst_mac']);
    const clientMac = clientMacRaw != null ? String(clientMacRaw).trim() : '';
    const serverMac = serverMacRaw != null ? String(serverMacRaw).trim() : '';

    const clientPort = toPortString(getField(row, ['Client Port', 'client_port', 'ClientPort', 'src port', 'Src Port', 'src_port']));
    const serverPort = toPortString(getField(row, ['Server Port', 'server_port', 'ServerPort', 'dst port', 'Dst Port', 'dst_port']));

    const clientBytes = toNumber(getField(row, ['Client Bytes', 'tx bytes', 'tx_bytes', 'Tx Bytes', 'TX Bytes', 'tx']));
    const serverBytes = toNumber(getField(row, ['Server Bytes', 'rx bytes', 'rx_bytes', 'Rx Bytes', 'RX Bytes', 'rx']));
    let totalBytes = clientBytes + serverBytes;
    if (Number.isNaN(totalBytes)) totalBytes = toNumber(getField(row, ['Bytes', 'bytes']));

    const packets = Math.floor(toNumber(getField(row, ['Packets', 'packets'])));
    const protocol = getField(row, ['Protocol', 'protocol']) || '';
    const portsRaw = getField(row, ['Ports', 'ports']);
    const portsRawStr = (portsRaw === null || typeof portsRaw === 'undefined') ? '' : String(portsRaw).trim();
    const isPlaceholderPorts = !portsRawStr || portsRawStr === '-:-' || portsRawStr === ':-' || portsRawStr === '-:' || portsRawStr === 'n/a';
    const ports = isPlaceholderPorts ? `${clientPort}:${serverPort}` : portsRawStr;

    const startTime = toNumber(getField(row, ['Start Time', 'start_time', 'start', 'timestamp']));
    const duration = toNumber(getField(row, ['Duration', 'duration']));

    ensureNode(a);
    ensureNode(b);

    const nodeA = nodes.find((n) => n && String(n.id) === a);
    const nodeB = nodes.find((n) => n && String(n.id) === b);
    if (nodeA) {
      nodeA.sentBytes += clientBytes;
      nodeA.receivedBytes += serverBytes;
      nodeA.totalBytes += totalBytes;
    }
    if (nodeB) {
      nodeB.sentBytes += serverBytes;
      nodeB.receivedBytes += clientBytes;
      nodeB.totalBytes += totalBytes;
    }

    addMacNode(clientMac);
    addMacNode(serverMac);
    if (clientMac) linkMacToIp(clientMac, a, packets);
    if (serverMac) linkMacToIp(serverMac, b, packets);

    const ordered = [a, b].sort();
    const minId = ordered[0];
    const baseCurv = 0.25;
    const curvatureAB = (a === minId) ? baseCurv : -baseCurv;
    const curvatureBA = -curvatureAB;

    const pushDirected = (src, dst, bytes, curv, dir, fromMac, toMac) => {
      const safeBytes = Number(bytes) || 0;
      const safePackets = Number(packets) || 0;
      if (!(safeBytes > 0) && !(safePackets > 0)) return;
      const dirKey = `${src}→${dst}`;
      const linkId = `${src}->${dst}:${dir}`;
      const existing = linkMap.get(dirKey);
      if (existing) {
        existing.value += safeBytes;
        if (!existing.protocol && protocol) existing.protocol = protocol;
        if (ports) {
          const prevPorts = existing.ports == null ? '' : String(existing.ports).trim();
          const prevIsPlaceholder = !prevPorts || prevPorts === '-:-' || prevPorts === ':-' || prevPorts === '-:' || prevPorts === 'n/a';
          if (!existing.ports || prevIsPlaceholder) existing.ports = ports;
        }
        if (safePackets) existing.packets = (existing.packets || 0) + safePackets;
        if (startTime && duration) existing.duration = Math.max(existing.duration || 0, duration);
        if (fromMac && (!existing.fromMac || existing.fromMac === '-' || existing.fromMac === '')) existing.fromMac = String(fromMac);
        if (toMac && (!existing.toMac || existing.toMac === '-' || existing.toMac === '')) existing.toMac = String(toMac);
      } else {
        const linkObj = {
          id: linkId,
          timestamp: startTime,
          source: src,
          target: dst,
          value: safeBytes,
          gas: 0,
          protocol,
          ports,
          duration,
          packets: safePackets,
          metaType: 'pcap',
          curvature: curv,
          pcapDir: dir,
          fromMac: fromMac ? String(fromMac) : undefined,
          toMac: toMac ? String(toMac) : undefined,
        };
        linkMap.set(dirKey, linkObj);
        links.push(linkObj);
      }
      addNeighbor(src, dst);
    };

    pushDirected(a, b, clientBytes, curvatureAB, 'tx', clientMac, serverMac);
    pushDirected(b, a, serverBytes, curvatureBA, 'rx', serverMac, clientMac);
  });

  nodes.forEach((n) => {
    const id = n && n.id ? String(n.id) : '';
    const set = neighborMap.get(id);
    if (set && set.size >= 0) n.linkCount = set.size;
  });

  return { nodes, links };
}

export default function PcapDetailsPage() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { uploadId: uploadIdParam } = useParams();
  const uploadId = useMemo(() => String(uploadIdParam || '').trim(), [uploadIdParam]);
  const uploadIds = useMemo(() => {
    try {
      const params = new URLSearchParams(location && location.search ? String(location.search) : '');
      const raw = String(params.get('upload_ids') || '').trim();
      const ids = raw
        ? raw.split(',').map((s) => String(s || '').trim()).filter(Boolean)
        : (uploadId ? [uploadId] : []);
      return Array.from(new Set(ids));
    } catch (e) {
      return uploadId ? [uploadId] : [];
    }
  }, [location, uploadId]);
  const isCombined = uploadIds.length > 1;
  const { isAuthenticated, authChecked } = useAuth();

  useEffect(() => {
    const resizeContainer = () => {
      try {
        const container = document.getElementById('pcapDetailsContainer');
        if (!container) return;

        const vv = (typeof window !== 'undefined' && window.visualViewport) ? window.visualViewport : null;
        const totalHeight = (vv && Number(vv.height)) ? Number(vv.height) : window.innerHeight;

        const e3dHeader = document.getElementById('e3dHeader');
        const adBannerContainer = document.getElementById('adBannerContainer');
        const premiumBannerContainer = document.getElementById('premiumBannerContainer');

        const otherHeight =
          (e3dHeader ? e3dHeader.clientHeight : 0) +
          (adBannerContainer ? adBannerContainer.clientHeight : 0) +
          (premiumBannerContainer ? premiumBannerContainer.clientHeight : 0);

        const availableHeight = totalHeight - otherHeight - 20;
        const clampedHeight = availableHeight > 0 ? availableHeight : totalHeight;

        container.style.height = `${clampedHeight}px`;
        container.style.maxHeight = `${clampedHeight}px`;
      } catch (e) {
      }
    };

    try {
      const vv = (typeof window !== 'undefined' && window.visualViewport) ? window.visualViewport : null;

      window.addEventListener('resize', resizeContainer);
      if (vv) {
        try { vv.addEventListener('resize', resizeContainer); } catch (e) {}
        try { vv.addEventListener('scroll', resizeContainer); } catch (e) {}
      }

      resizeContainer();
      try { setTimeout(resizeContainer, 0); } catch (e) {}

      return () => {
        window.removeEventListener('resize', resizeContainer);
        if (vv) {
          try { vv.removeEventListener('resize', resizeContainer); } catch (e) {}
          try { vv.removeEventListener('scroll', resizeContainer); } catch (e) {}
        }
      };
    } catch (e) {
      return () => {};
    }
  }, []);

  const [upload, setUpload] = useState(null);
  const [stories, setStories] = useState([]);
  const [theses, setTheses] = useState([]);
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [tokenList, setTokenList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [graphLoading, setGraphLoading] = useState(false);
  const [error, setError] = useState('');

  const [openStory, setOpenStory] = useState(null);
  const [openThesis, setOpenThesis] = useState(null);

  const graphRef = useRef(null);
  const evidenceRootRef = useRef(null);
  const graphBoxRef = useRef(null);
  const [graphWidth, setGraphWidth] = useState(640);
  const [graphHeight, setGraphHeight] = useState(360);
  const lastSplitClientYRef = useRef(null);
  const [splitRatio, setSplitRatio] = useState(() => {
    try {
      if (typeof window === 'undefined') return 0.5;
      const raw = window.localStorage.getItem('pcapEvidenceSplit');
      const v = Number(raw);
      if (!Number.isFinite(v)) return 0.7;
      if (v <= 0.55 || v >= 0.9) return 0.7;
      return v;
    } catch (e) {
      return 0.7;
    }
  });

  const hashColorHsl = useCallback((str) => {
    const s = String(str || '');
    let h = 2166136261;
    for (let i = 0; i < s.length; i += 1) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    const hue = ((h >>> 0) % 360);
    return `hsl(${hue}, 75%, 55%)`;
  }, []);

  const handleNodeClickLocal = useCallback((dataSource, g, gRef, nodeId) => {
    try {
      const gg = g && g.nodes ? g : (graphData || { nodes: [], links: [] });
      const id = nodeId != null ? String(nodeId).trim() : '';
      if (!id) return;
      const node = Array.isArray(gg.nodes) ? gg.nodes.find((n) => n && String(n.id) === id) : null;
      if (!node) return;
      const api = gRef && gRef.current ? gRef.current : null;
      if (!api) return;

      if (typeof api.centerAt === 'function') {
        api.centerAt(node.x || 0, node.y || 0, 800);
      }
      if (typeof api.zoom === 'function') {
        api.zoom(6, 800);
      }
      if (typeof api.cameraPosition === 'function') {
        const distRatio = 1 + 80 / Math.hypot(node.x || 0, node.y || 0, node.z || 0);
        api.cameraPosition(
          { x: (node.x || 0) * distRatio, y: (node.y || 0) * distRatio, z: (node.z || 0) * distRatio },
          node,
          900,
        );
      }
    } catch (e) {
    }
  }, [graphData]);

  const openStoryEvidence = openStory && openStory.evidence_json ? safeJsonParse(String(openStory.evidence_json)) : null;
  const openThesisEvidence = openThesis && openThesis.evidence_json ? safeJsonParse(String(openThesis.evidence_json)) : null;

  const loadMeta = useCallback(async () => {
    if (!uploadIds || !uploadIds.length) return;
    if (!isAuthenticated) return;

    setLoading(true);
    setError('');
    try {
      if (!isCombined) {
        const id = uploadIds[0];
        const [upResp, stResp, thResp] = await Promise.all([
          axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}`, { withCredentials: true, timeout: 20000 }),
          axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}/stories`, { withCredentials: true, timeout: 20000 }),
          axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}/theses`, { withCredentials: true, timeout: 20000 }),
        ]);
        setUpload(upResp && upResp.data ? upResp.data : null);
        setStories(stResp && stResp.data && Array.isArray(stResp.data.items) ? stResp.data.items : []);
        setTheses(thResp && thResp.data && Array.isArray(thResp.data.items) ? thResp.data.items : []);
        return;
      }

      const perUpload = await Promise.all(uploadIds.map(async (id) => {
        const [upResp, stResp, thResp] = await Promise.all([
          axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}`, { withCredentials: true, timeout: 20000 }),
          axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}/stories`, { withCredentials: true, timeout: 20000 }),
          axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}/theses`, { withCredentials: true, timeout: 20000 }),
        ]);
        const upload = upResp && upResp.data ? upResp.data : null;
        const stories = stResp && stResp.data && Array.isArray(stResp.data.items) ? stResp.data.items : [];
        const theses = thResp && thResp.data && Array.isArray(thResp.data.items) ? thResp.data.items : [];
        return { id, upload, stories, theses };
      }));

      const uploads = perUpload.map((x) => x.upload).filter(Boolean);
      const allStories = perUpload.flatMap((x) => Array.isArray(x.stories) ? x.stories : []);
      const allTheses = perUpload.flatMap((x) => Array.isArray(x.theses) ? x.theses : []);

      const parseCreatedAtMs = (row) => {
        try {
          const raw = row && row.created_at != null ? String(row.created_at) : '';
          const ms = raw ? Date.parse(raw) : NaN;
          return Number.isFinite(ms) ? ms : 0;
        } catch (e) {
          return 0;
        }
      };

      allStories.sort((a, b) => parseCreatedAtMs(b) - parseCreatedAtMs(a));
      allTheses.sort((a, b) => parseCreatedAtMs(b) - parseCreatedAtMs(a));

      const totalFlows = uploads.reduce((sum, u) => sum + (u && u.flow_count != null ? Number(u.flow_count) : 0), 0);
      const safeTotalFlows = Number.isFinite(totalFlows) ? totalFlows : 0;

      const statusSet = new Set(uploads.map((u) => (u && u.status ? String(u.status) : '')).filter(Boolean));
      const combinedStatus = statusSet.has('failed')
        ? 'failed'
        : (statusSet.has('ingesting') || statusSet.has('stories_running') || statusSet.has('thesis_running') || statusSet.has('ready'))
          ? 'processing'
          : 'done';

      const firstCreatedAt = uploads
        .map((u) => (u && u.created_at ? String(u.created_at) : ''))
        .filter(Boolean)
        .sort()[0] || '';

      setUpload({
        upload_id: uploadIds[0],
        filename: `Combined uploads (${uploadIds.length})`,
        status: combinedStatus,
        created_at: firstCreatedAt || null,
        flow_count: safeTotalFlows,
        error_message: '',
        combined_upload_ids: uploadIds,
      });
      setStories(allStories);
      setTheses(allTheses);
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.message
        ? String(e.response.data.message)
        : (e && e.message ? String(e.message) : String(e));
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [uploadIds, isCombined, isAuthenticated]);

  const loadGraph = useCallback(async () => {
    if (!uploadIds || !uploadIds.length) return;
    if (!isAuthenticated) return;

    setGraphLoading(true);
    setError('');
    try {
      let csvText = '';
      if (!isCombined) {
        const id = uploadIds[0];
        const resp = await axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}/graphCsv`, {
          withCredentials: true,
          responseType: 'text',
          timeout: 60000,
        });
        csvText = resp && typeof resp.data === 'string' ? resp.data : '';
        if (!csvText) throw new Error('Empty graph CSV');
      } else {
        let merged = '';
        let headerLine = '';
        for (let i = 0; i < uploadIds.length; i += 1) {
          const id = uploadIds[i];
          const resp = await axios.get(`${SERVER_NAME}/pcap/uploads/${encodeURIComponent(id)}/graphCsv`, {
            withCredentials: true,
            responseType: 'text',
            timeout: 60000,
          });
          const part = resp && typeof resp.data === 'string' ? resp.data : '';
          if (!part) continue;
          const lines = String(part || '').split(/\r?\n/).filter((l) => String(l || '').trim() !== '');
          if (!lines.length) continue;
          if (!headerLine) {
            headerLine = String(lines[0] || '').trim();
            merged += `${headerLine}\n`;
          }
          const body = lines.slice(1);
          if (body.length) merged += `${body.join('\n')}\n`;
        }
        csvText = merged;
        if (!csvText) throw new Error('Empty graph CSV');
      }

      let Papa = null;
      try {
        // eslint-disable-next-line global-require
        Papa = require('papaparse');
      } catch (e) {
        Papa = null;
      }
      if (!Papa) throw new Error('Missing CSV parser dependency (papaparse)');

      const parsed = Papa.parse(String(csvText || ''), {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
      });
      const rows = parsed && Array.isArray(parsed.data) ? parsed.data : [];

      const builtGraph = buildPcapGraphFromRows(rows);
      setGraphData(builtGraph);

      const listRows = (builtGraph && Array.isArray(builtGraph.nodes) ? builtGraph.nodes : [])
        .filter((n) => n && String(n.metaType || '') !== 'pcap_mac')
        .map((n) => {
          const id = n && n.id != null ? String(n.id) : '';
          return {
            id,
            label: id,
            symbol: id,
            displayId: n && n.displayId ? n.displayId : undefined,
            color: hashColorHsl(id),
            sentBytes: Number(n && n.sentBytes) || 0,
            receivedBytes: Number(n && n.receivedBytes) || 0,
            totalBytes: Number(n && n.totalBytes) || 0,
          };
        })
        .sort((a, b) => (Number(b.totalBytes) || 0) - (Number(a.totalBytes) || 0));
      setTokenList(listRows);
    } catch (e) {
      const msg = e && e.response && e.response.data && e.response.data.message
        ? String(e.response.data.message)
        : (e && e.message ? String(e.message) : String(e));
      setError(msg);
    } finally {
      setGraphLoading(false);
    }
  }, [uploadIds, isCombined, isAuthenticated, hashColorHsl]);

  useEffect(() => {
    if (!authChecked) return;
    if (!isAuthenticated) return;
    loadMeta();
    loadGraph();
  }, [authChecked, isAuthenticated, loadMeta, loadGraph]);

  useEffect(() => {
    if (!authChecked) return;
    if (!isAuthenticated) return;
    if (!uploadIds || !uploadIds.length) return;
    if (isCombined) return;

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        await loadMeta();
      } catch (e) {
      }
    };

    const st = upload && upload.status ? String(upload.status) : '';
    const shouldPoll = st && (st === 'ingesting' || st === 'ready' || st === 'stories_running' || st === 'thesis_running');
    if (!shouldPoll) return undefined;

    const id = setInterval(() => { tick(); }, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [authChecked, isAuthenticated, uploadIds, upload, loadMeta, isCombined]);

  useEffect(() => {
    const el = graphBoxRef.current;
    if (!el) return undefined;

    const recompute = () => {
      try {
        const rect = el.getBoundingClientRect();
        const w = rect.width;
        const h = rect.height;
        if (Number.isFinite(w) && w > 100) setGraphWidth(Math.floor(w));
        if (Number.isFinite(h) && h > 100) setGraphHeight(Math.floor(h));
      } catch (e) {
      }
    };

    recompute();
    let ro = null;
    try {
      ro = new ResizeObserver(() => recompute());
      ro.observe(el);
    } catch (e) {
    }

    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('resize', recompute);
      try {
        if (ro) ro.disconnect();
      } catch (e) {
      }
    };
  }, []);

  const applySplitFromPointer = useCallback((clientY) => {
    const el = evidenceRootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const totalH = rect.height || 0;
    if (!totalH) return;
    const y = clientY - rect.top;
    const next = Math.max(0.55, Math.min(0.9, y / totalH));
    setSplitRatio(next);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('pcapEvidenceSplit', String(next));
      }
    } catch (e) {
    }
  }, []);

  const startSplitDrag = useCallback((e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
    } catch (err) {
    }

    const move = (ev) => {
      const y = ev && ev.clientY != null ? ev.clientY : null;
      if (y == null) return;
      lastSplitClientYRef.current = y;
      applySplitFromPointer(y);
    };

    const up = () => {
      const y = lastSplitClientYRef.current;
      if (y != null) {
        applySplitFromPointer(y);
      }
      try {
        requestAnimationFrame(() => {
          try {
            if (graphRef && graphRef.current && typeof graphRef.current.refresh === 'function') {
              graphRef.current.refresh();
            }
          } catch (err) {
          }
        });
      } catch (err) {
      }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);

    const initialY = e && e.clientY != null ? e.clientY : null;
    if (initialY != null) {
      lastSplitClientYRef.current = initialY;
      applySplitFromPointer(initialY);
    }
  }, [applySplitFromPointer, graphRef]);

  if (!authChecked) {
    return (
      <div id="pcapDetailsContainer" className={classes.root}>
        <div className={classes.hero}>
          <div className={classes.heroTopRow}>
            <div>
              <div className={classes.heroTitle}>PCAP Details</div>
              <div className={classes.heroSubtitle}>Checking authentication…</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div id="pcapDetailsContainer" className={classes.root}>
        <div className={classes.hero}>
          <div className={classes.heroTopRow}>
            <div>
              <div className={classes.heroTitle}>PCAP Details</div>
              <div className={classes.heroSubtitle}>You must be logged in to view upload results.</div>
              <div style={{ marginTop: 12 }}>
                <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
                  Login
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const status = upload && upload.status ? String(upload.status) : '';

  return (
    <div id="pcapDetailsContainer" className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.heroTopRow}>
          <div>
            <div className={classes.heroTitle}>PCAP Details</div>
            <div className={classes.heroSubtitle}>
              {upload && upload.filename ? String(upload.filename) : 'PCAP upload'}
            </div>
            <div className={classes.heroMetaRow}>
              <Chip className={classes.heroMetaChip} label={`Status: ${status || 'n/a'}`} />
              {(upload && upload.created_at) ? <Chip className={classes.heroMetaChip} label={`Created: ${String(upload.created_at)}`} /> : null}
              {(upload && upload.flow_count != null) ? <Chip className={classes.heroMetaChip} label={`Flows: ${String(upload.flow_count)}`} /> : null}
              <Chip className={classes.heroMetaChip} label={isCombined ? `Uploads: ${uploadIds.length}` : `Upload: ${String(uploadId).slice(0, 8)}…`} />
              {(loading || graphLoading) ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <CircularProgress size={14} style={{ color: '#cfd3ff' }} />
                  <span>Loading…</span>
                </span>
              ) : null}
            </div>
            {!!error ? (
              <div className={classes.error}>{error}</div>
            ) : null}
            {(upload && upload.error_message) ? (
              <div className={classes.error}>Server error_message: {String(upload.error_message)}</div>
            ) : null}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button className={classes.ghostButton} variant="outlined" onClick={() => navigate('/pcap/uploads')}>
              Back
            </Button>
            <Button className={classes.ghostButton} variant="outlined" onClick={() => { loadMeta(); loadGraph(); }} disabled={loading || graphLoading}>
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.mainGrid}>
        <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
          <div className={classes.feedPanel} style={{ flex: '0 0 auto', minHeight: 0, maxHeight: '42%' }}>
            <div className={classes.feedHeader}>
              <div className={classes.sectionTitle}>Thesis</div>
              <div className={classes.sectionHint}>{Array.isArray(theses) ? theses.length : 0}</div>
            </div>
            <div className={classes.feedBody} style={{ flex: '0 1 auto' }}>
              {(Array.isArray(theses) ? theses : []).map((t) => (
                <div
                  key={(t && t.thesis_id) ? String(t.thesis_id) : Math.random()}
                  className={classes.feedRow}
                  style={{ position: 'relative', paddingRight: 90 }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div className={classes.feedRowTitle} style={{ marginBottom: 0, fontWeight: 400, lineHeight: 1.5, paddingRight: 8 }}>
                      {t && t.thesis ? String(t.thesis) : '(no thesis text)'}
                    </div>
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ position: 'absolute', top: 10, right: 10, textTransform: 'none', fontWeight: 700, color: '#cfd3ff', borderColor: 'rgba(255,255,255,0.14)' }}
                      onClick={(e) => {
                        try { e.stopPropagation(); } catch (err) {}
                        setOpenThesis(t);
                      }}
                    >
                      Open
                    </Button>
                  </div>

                  <div className={classes.feedRowMeta}>
                    <Chip className={classes.chipType} label={t && t.time_horizon ? String(t.time_horizon) : 'horizon'} />
                    <Chip className={classes.chipChain} label={t && t.confidence != null ? `confidence=${t.confidence}` : 'confidence=n/a'} />
                    <span>{t && t.created_at ? String(t.created_at) : ''}</span>
                    {t && t.status ? <span>{`status=${String(t.status)}`}</span> : null}
                  </div>
                </div>
              ))}
              {(!theses || !theses.length) ? (
                <div style={{ padding: 10, color: '#9ea4d8', fontSize: 12 }}>No thesis yet.</div>
              ) : null}
            </div>
          </div>

          <div className={classes.feedPanel} style={{ flex: 1, minHeight: 0 }}>
            <div className={classes.feedHeader}>
              <div className={classes.sectionTitle}>Stories</div>
              <div className={classes.sectionHint}>{Array.isArray(stories) ? stories.length : 0}</div>
            </div>
            <div className={classes.feedBody}>
              {(Array.isArray(stories) ? stories : []).map((s) => (
                <div
                  key={(s && s.story_id) ? String(s.story_id) : Math.random()}
                  className={classes.feedRow}
                  style={{ position: 'relative', paddingRight: 90 }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div className={classes.feedRowTitle} style={{ marginBottom: 0 }}>
                      {s && s.title ? String(s.title) : '(untitled)'}
                    </div>
                    <Button
                      variant="outlined"
                      size="small"
                      style={{ position: 'absolute', top: 10, right: 10, textTransform: 'none', fontWeight: 700, color: '#cfd3ff', borderColor: 'rgba(255,255,255,0.14)' }}
                      onClick={(e) => {
                        try { e.stopPropagation(); } catch (err) {}
                        setOpenStory(s);
                      }}
                    >
                      Open
                    </Button>
                  </div>

                  <div className={classes.feedRowMeta}>
                    <Chip className={classes.chipType} label={s && s.story_type ? String(s.story_type) : 'story'} />
                    <Chip className={classes.chipChain} label={s && s.severity ? String(s.severity) : 'n/a'} />
                    <span>{s && s.created_at ? String(s.created_at) : ''}</span>
                    {s && s.status ? <span>{`status=${String(s.status)}`}</span> : null}
                  </div>

                  <div className={classes.subtle} style={{ marginTop: 8, fontSize: 12, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                    {s && s.summary ? String(s.summary) : ''}
                  </div>
                </div>
              ))}
              {(!stories || !stories.length) ? (
                <div style={{ padding: 10, color: '#9ea4d8', fontSize: 12 }}>No stories yet.</div>
              ) : null}
            </div>
          </div>
        </div>

        <div className={classes.forensicsPanel}>
          <div className={classes.forensicsHeader}>
            <div>
              <div className={classes.forensicsTitle}>Evidence Summary</div>
              <div className={classes.forensicsTokenLabel}>PCAP upload</div>
            </div>
            <Button
              className={classes.ghostButton}
              variant="outlined"
              size="small"
              style={{ padding: '6px 10px', borderRadius: 10 }}
              onClick={() => {
                try {
                  if (isCombined) {
                    const qs = new URLSearchParams({ upload_ids: uploadIds.join(',') }).toString();
                    navigate(`/pcap?${qs}`);
                  } else {
                    navigate(`/pcap?upload_id=${encodeURIComponent(uploadId)}`);
                  }
                } catch (e) {
                }
              }}
              endIcon={<OpenInNewIcon style={{ fontSize: 16 }} />}
            >
              Open graph
            </Button>
          </div>

          <div className={classes.forensicsTokenValue}>{isCombined ? uploadIds.join(', ') : uploadId}</div>

          <div className={classes.evidenceSplitRoot} ref={evidenceRootRef}>
            <div
              className={classes.evidenceGraphWrap}
              style={{ flex: `${splitRatio} 1 0%`, flexBasis: 0 }}
            >
              <div ref={graphBoxRef} style={{ position: 'absolute', inset: 0 }}>
                {graphLoading ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress style={{ color: '#cfd3ff' }} size={18} />
                  </div>
                ) : (
                  ((graphData && Array.isArray(graphData.nodes) && graphData.nodes.length > 0) ? (
                    <GraphComponent
                      isMobile={false}
                      is3DMode
                      graphData={graphData}
                      hiddenNodeIds={new Set()}
                      graphRef={graphRef}
                      handleBackgroundClick={() => {}}
                      handleNodeDragEnd={() => {}}
                      handleNodeDrag={() => {}}
                      handleEngineStop={() => {}}
                      handleZoom={() => {}}
                      handleClick={() => {}}
                      handleNodeRightClick={() => {}}
                      handleNodeDoubleClick={() => {}}
                      dataSource={PCAP}
                      filterNFT={false}
                      maxValue={null}
                      imageSize={18}
                      stopCameraSpin={() => {}}
                      startCameraSpin={() => {}}
                      setActiveLinkId={() => {}}
                      handleNodeClick={handleNodeClickLocal}
                      showText
                      showAddressLabels
                      showNames
                      showChainLabels={false}
                      width={graphWidth}
                      height={graphHeight}
                      currentView="/pcap"
                      spinOnMount={false}
                      pcapLinkStyle="collapsed_straight"
                      pcapShowLabels
                      pcapLabelSize={4}
                      pcapGroupEnabled={false}
                      labelSize={10}
                      pcapSelectedLinkKeys={null}
                    />
                  ) : null)
                )}
              </div>
            </div>

            <div className={classes.evidenceSplitter} onPointerDown={startSplitDrag} role="separator" aria-orientation="horizontal">
              <div className={classes.evidenceSplitterBar} />
            </div>

            <div
              className={classes.evidenceListWrap}
              style={{ flex: `${1 - splitRatio} 1 0%`, flexBasis: 0 }}
            >
              <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography style={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.6, textTransform: 'uppercase', color: '#e5e7ff' }}>
                  Addresses
                </Typography>
                <Typography style={{ fontSize: 12, color: '#9ea4d8' }}>{Array.isArray(tokenList) ? tokenList.length : 0}</Typography>
              </div>

              <div className={classes.evidenceListBody}>
                <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <NodeList
                    graphData={graphData}
                    graphRef={graphRef}
                    dataSource={PCAP}
                    hiddenNodeIds={new Set()}
                    onToggleNodeHidden={null}
                    linkCount={new Map()}
                    getTransactions={() => {}}
                    handleNodeClick={handleNodeClickLocal}
                    stopCameraSpin={() => {}}
                    isMobile={false}
                    isPortrait={false}
                    tokenType="Token"
                    tokenList={tokenList}
                    viewGraph
                    rangeSelected={null}
                    organicLayout={false}
                    tokenView="links"
                    showNames
                    pcapShowMac={false}
                    showAddressNodes
                    showPriceColumns={false}
                    useColorDots
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={!!openStory}
        onClose={() => setOpenStory(null)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ style: { height: '50vh', maxHeight: '50vh', minHeight: '50vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      >
        <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <span>PCAP Story</span>
          <IconButton onClick={() => setOpenStory(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ flex: '1 1 auto', overflowY: 'auto' }}>
          <Typography style={{ fontWeight: 900, marginBottom: 8 }}>
            {openStory && openStory.title ? String(openStory.title) : '(untitled)'}
          </Typography>
          <Typography style={{ opacity: 0.8, fontFamily: 'monospace', fontSize: 12, marginBottom: 10 }}>
            story_id: {openStory && openStory.story_id ? String(openStory.story_id) : ''}
            {' | '}created_at: {openStory && openStory.created_at ? String(openStory.created_at) : ''}
            {' | '}type: {openStory && openStory.story_type ? String(openStory.story_type) : ''}
            {' | '}severity: {openStory && openStory.severity ? String(openStory.severity) : ''}
            {' | '}status: {openStory && openStory.status ? String(openStory.status) : ''}
          </Typography>
          <Typography style={{ whiteSpace: 'pre-wrap' }}>
            {openStory && openStory.summary ? String(openStory.summary) : ''}
          </Typography>
          {openStory && openStory.error_message ? (
            <Typography style={{ marginTop: 10, color: '#b00020', whiteSpace: 'pre-wrap' }}>
              error_message: {String(openStory.error_message)}
            </Typography>
          ) : null}
          {(openStoryEvidence != null) ? (
            <pre style={{ marginTop: 14, fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(openStoryEvidence, null, 2)}
            </pre>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!openThesis}
        onClose={() => setOpenThesis(null)}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ style: { height: '50vh', maxHeight: '50vh', minHeight: '50vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      >
        <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, color: '#fff' }}>
          <span>PCAP Thesis</span>
          <IconButton onClick={() => setOpenThesis(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers style={{ color: '#fff', flex: '1 1 auto', overflowY: 'auto' }}>
          <Typography style={{ fontWeight: 400, marginBottom: 8, lineHeight: 1.6 }}>
            {openThesis && openThesis.thesis ? String(openThesis.thesis) : '(no thesis text)'}
          </Typography>
          <Typography style={{ opacity: 0.8, fontFamily: 'monospace', fontSize: 12, marginBottom: 10 }}>
            thesis_id: {openThesis && openThesis.thesis_id ? String(openThesis.thesis_id) : ''}
            {' | '}created_at: {openThesis && openThesis.created_at ? String(openThesis.created_at) : ''}
            {' | '}horizon: {openThesis && openThesis.time_horizon ? String(openThesis.time_horizon) : ''}
            {' | '}confidence: {openThesis && openThesis.confidence != null ? String(openThesis.confidence) : ''}
            {' | '}status: {openThesis && openThesis.status ? String(openThesis.status) : ''}
          </Typography>

          {openThesis && openThesis.entry_conditions ? (
            <Typography style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontWeight: 900 }}>Entry conditions:</span> {String(openThesis.entry_conditions)}
            </Typography>
          ) : null}
          {openThesis && openThesis.invalidation_conditions ? (
            <Typography style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontWeight: 900 }}>Invalidation conditions:</span> {String(openThesis.invalidation_conditions)}
            </Typography>
          ) : null}
          {openThesis && openThesis.risks ? (
            <Typography style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontWeight: 900 }}>Risks:</span> {String(openThesis.risks)}
            </Typography>
          ) : null}
          {openThesis && openThesis.error_message ? (
            <Typography style={{ marginTop: 10, color: '#b00020', whiteSpace: 'pre-wrap' }}>
              error_message: {String(openThesis.error_message)}
            </Typography>
          ) : null}
          {openThesis && Array.isArray(openThesis.story_ids) && openThesis.story_ids.length ? (
            <Typography style={{ marginTop: 10, opacity: 0.8, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
              story_ids: {openThesis.story_ids.map((x) => String(x)).join(', ')}
            </Typography>
          ) : null}
          {(openThesisEvidence != null) ? (
            <pre style={{ marginTop: 14, fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(openThesisEvidence, null, 2)}
            </pre>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
